import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as classnames from "classnames";
import {
  fetchOrganizationTerms,
  removeTermVersion,
  uploadOrganizationTermVersion,
} from "../../redux/organization/actions";
import { alertAdd } from "../../redux/actions";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles, Paper } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";

import { startOfDay } from "date-fns";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import plLocale from "date-fns/locale/pl";
import DateFnsUtils from "@date-io/date-fns";
import format from "date-fns/format";
import { ConfirmDialogMUI } from "../../components/confirm-dialog-mui";

const NO_VERSION_TO_REMOVE = null;

// A version is uploaded with all these language variants at once.
const TERMS_LANGUAGES = [
  { code: "pl", label: "PL" },
  { code: "en", label: "EN" },
];

const versionKey = (term) => term.versionId || term.id;

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  container: {
    padding: "1.5rem",
    marginTop: 10,
  },
  rootContainer: {
    height: "100%",
    overflow: "auto",
  },
}));

const TermsAdminPanel = () => {
  const classes = useStyles();

  const [termName, setTermName] = useState("");
  const [termsActiveSince, setTermsActiveSince] = useState();
  const [filePl, setFilePl] = useState();
  const [fileEn, setFileEn] = useState();
  const terms = useSelector((s) => s.terms);

  const [versionToRemove, setVersionToRemove] = useState(NO_VERSION_TO_REMOVE);

  const [formRef, setFormRef] = useState();

  const dispatch = useDispatch();

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchOrganizationTerms());
  }, []);

  const handleUploadedFile = (files, setFile) => {
    if (files.length > 0) {
      setFile(files[0]);
    }
  };

  const askToRemove = (versionId) => {
    setVersionToRemove(versionId);
  };

  const remove = async (versionId) => {
    try {
      await dispatch(removeTermVersion(versionId));
      setVersionToRemove(NO_VERSION_TO_REMOVE);
      await dispatch(fetchOrganizationTerms());
    } catch (error) {
      let errorMessage = `Błąd usuwania wersji regulaminu, (${error.message})`;
      if (error.response && error.response.status === 400) {
        if (error.response.data.message === "cant-remove-used-terms") {
          errorMessage =
            "Błąd usuwania wersji regulaminu, ktoś już zaakceptował ten regulamin.";
        }
      }
      dispatch(
        alertAdd({
          text: errorMessage,
          isError: true,
          timeout: 4000,
        })
      );
    }
  };

  const versionWithSameActiveSinceExists = (activeSince) => {
    if (!terms || !activeSince) {
      return false;
    }
    return terms.some(
      (term) =>
        term.removedAt == null &&
        new Date(term.activeSince).getTime() ===
          new Date(activeSince).getTime()
    );
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!isVersionValid()) {
      return;
    }

    if (versionWithSameActiveSinceExists(termsActiveSince)) {
      dispatch(
        alertAdd({
          text:
            "Istnieje już wersja regulaminu rozpoczynająca się od tej daty. Zmień datę rozpoczęcia obowiązywania lub usuń istniejącą wersję.",
          isError: true,
          timeout: 4000,
        })
      );
      return;
    }

    setBusy(true);
    const uploaded = await dispatch(
      uploadOrganizationTermVersion({
        filePl,
        fileEn,
        name: termName,
        activeSince: termsActiveSince,
      })
    );
    setBusy(false);

    if (uploaded) {
      formRef.reset();
      setTermName("");
      setTermsActiveSince("");
      setFilePl(null);
      setFileEn(null);
      await dispatch(fetchOrganizationTerms());
    } else {
      dispatch(
        alertAdd({
          text: "Nie udało się dodać wersji regulaminu.",
          isError: true,
          timeout: 4000,
        })
      );
    }
  };

  const isVersionValid = () => {
    return (
      !!termName &&
      !!filePl &&
      !!fileEn &&
      termsActiveSince !== undefined &&
      termsActiveSince !== ""
    );
  };

  const handleActiveSinceChange = (date) => {
    setTermsActiveSince(new Date(startOfDay(date)).toISOString());
  };

  const renderFileSlot = (id, label, file, setFile) => (
    <Box mt={3}>
      <div>{label} *</div>
      <input
        style={{ display: "none" }}
        id={id}
        type="file"
        onChange={(event) => {
          handleUploadedFile(event.target.files, setFile);
          event.target.value = null;
        }}
        accept="application/pdf"
      />
      <label htmlFor={id}>
        <Button variant="contained" component="span">
          Wgraj
        </Button>
      </label>
      {file && (
        <span>
          {` ${file.name} `}
          <Button variant="text" onClick={() => setFile(null)}>
            Usuń plik
          </Button>
        </span>
      )}
    </Box>
  );

  const versions = buildVersions(terms);

  return (
    <Box className={classes.rootContainer}>
      <Grid container direction="column">
        <Box>
          <Typography variant="h6" gutterBottom>
            Zarządzanie regulaminami
          </Typography>
        </Box>

        <hr />

        <Grid item lg={12} xl={8}>
          <form
            ref={(el) => setFormRef(el)}
            onSubmit={submit}
            noValidate
            autoComplete="off"
          >
            <Typography variant="subtitle1" component="h2">
              Dodaj wersję regulaminu
            </Typography>
            <Box mt={2}>
              <TextField
                required
                label="Nazwa regulaminu"
                variant="outlined"
                type="text"
                value={termName}
                fullWidth
                onChange={(e) => setTermName(e.target.value)}
              />
            </Box>

            {renderFileSlot(
              "terms-file-pl",
              "Wgraj polski plik regulaminu (PL)",
              filePl,
              setFilePl
            )}
            {renderFileSlot(
              "terms-file-en",
              "Wgraj angielski plik regulaminu (EN)",
              fileEn,
              setFileEn
            )}

            <Box mt={3}>
              <MuiPickersUtilsProvider locale={plLocale} utils={DateFnsUtils}>
                <KeyboardDatePicker
                  InputProps={{
                    readOnly: true,
                  }}
                  label="Data rozpoczęcia obowiązywania regulaminu"
                  size="small"
                  margin="none"
                  format="dd/MM/yyyy"
                  value={termsActiveSince || null}
                  inputVariant="outlined"
                  onChange={(date) => handleActiveSinceChange(date)}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  cancelLabel={"Anuluj"}
                  okLabel={"Zatwierdź"}
                  fullWidth
                  required
                />
              </MuiPickersUtilsProvider>
            </Box>
            <div style={{ marginTop: "2px" }}>* Pola wymagane</div>
            <Box mt={1} display="flex">
              <Button variant={"contained"} fullWidth>
                Anuluj
              </Button>
              <Button
                className={classnames({ "is-loading": busy })}
                type="submit"
                variant={"contained"}
                disabled={!isVersionValid() || busy}
                fullWidth
                style={{
                  backgroundColor: !isVersionValid() ? "" : "blue",
                  color: !isVersionValid() ? "" : "white",
                }}
              >
                {busy && (
                  <CircularProgress size={24} style={{ color: "white" }} />
                )}
                {!busy && "Dodaj wersję"}
              </Button>
            </Box>
          </form>
        </Grid>
        <hr />
        <Grid item xs={12}>
          <Typography variant="subtitle1" component="h1">
            Lista wersji regulaminu
          </Typography>
          <br />

          <ConfirmDialogMUI
            handleClose={() => {
              setVersionToRemove(NO_VERSION_TO_REMOVE);
            }}
            open={versionToRemove !== NO_VERSION_TO_REMOVE}
            text={`Czy na pewno usunąć tę wersję regulaminu (wszystkie języki)?`}
            yesAction={() => {
              remove(versionToRemove);
              setVersionToRemove(NO_VERSION_TO_REMOVE);
            }}
            noAction={() => setVersionToRemove(NO_VERSION_TO_REMOVE)}
          />
          <TableContainer component={Paper}>
            <Table aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell align="right">Nazwa</TableCell>
                  <TableCell align="right">Języki</TableCell>
                  <TableCell align="right">Aktywna od</TableCell>
                  <TableCell align="right">Opcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.versionId}>
                    <TableCell align="right">{version.name}</TableCell>
                    <TableCell align="right">
                      {TERMS_LANGUAGES.map((lang) => {
                        const variant = version.variantsByLanguage[lang.code];
                        return (
                          <span key={lang.code} style={{ marginLeft: 8 }}>
                            {lang.label}
                            {variant ? (
                              <>
                                {" "}
                                <a
                                  href={`/org-media/files${variant.link}`}
                                  target={"_blank"}
                                  rel="noreferrer"
                                >
                                  (podgląd)
                                </a>
                              </>
                            ) : (
                              " (brak)"
                            )}
                          </span>
                        );
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {version.activeSince
                        ? format(new Date(version.activeSince), "yyyy-MM-dd")
                        : ""}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip placement="top" title={"Usuń wersję"}>
                        <span>
                          <IconButton
                            onClick={() => askToRemove(version.versionId)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

// Groups the flat list of active terms variants into versions (by versionId), newest first.
const buildVersions = (terms) => {
  if (!terms) {
    return [];
  }

  const byVersion = {};
  terms
    .filter((term) => term.removedAt == null)
    .forEach((term) => {
      const key = versionKey(term);
      if (!byVersion[key]) {
        byVersion[key] = {
          versionId: key,
          name: term.name,
          activeSince: term.activeSince,
          variantsByLanguage: {},
        };
      }
      byVersion[key].variantsByLanguage[(term.language || "pl").toLowerCase()] =
        term;
    });

  return Object.values(byVersion).sort(
    (a, b) =>
      new Date(b.activeSince).getTime() - new Date(a.activeSince).getTime()
  );
};

export default TermsAdminPanel;
